import { useState, type FormEvent } from 'react';
import { Mail, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useHomepageSectionMutation } from '@/hooks/useHomepageSectionMutation';
import QuickEditInline from '@/components/quickedit/QuickEditInline';

interface NewsletterProps {
  section: any;
}

export default function Newsletter({ section }: NewsletterProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const headingId = `newsletter-heading-${section.id}`;

  const { isActive } = useQuickEdit();
  const mutation = useHomepageSectionMutation(section.id);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      await api.post('/newsletter/subscribe', { email });
      setStatus('success');
      setEmail('');
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message || 'Subscription failed. Please try again.');
    }
  }

  return (
    <section aria-labelledby={headingId} className="bg-linen">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Text */}
          <div>
            <Mail className="h-10 w-10 text-sage-400 mb-4" aria-hidden="true" />
            <h2
              id={headingId}
              className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
            >
              {isActive ? (
                <QuickEditInline
                  value={section.title || 'Stay Updated'}
                  onSave={(val) => mutation.mutateAsync({ title: val, content: section.content })}
                  label="Newsletter title"
                  as="span"
                  className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
                />
              ) : (section.title || 'Stay Updated')}
            </h2>
            <p className="mt-2 text-sage-600 max-w-lg">
              {isActive ? (
                <QuickEditInline
                  value={section.subtitle || 'Join our newsletter for updates, exclusive discounts, and new arrivals.'}
                  onSave={(val) => mutation.mutateAsync({ subtitle: val, content: section.content })}
                  label="Newsletter subtitle"
                  as="span"
                  className="text-sage-600"
                />
              ) : (section.subtitle || 'Join our newsletter for updates, exclusive discounts, and new arrivals.')}
            </p>
            <p className="mt-2 text-xs text-sage-500">
              No spam, ever. Unsubscribe anytime.
            </p>
          </div>

          {/* Form */}
          <div>
            {status === 'success' ? (
              <div
                className="rounded-lg bg-sage-100 p-6 text-sage-800"
                role="status"
              >
                <p className="font-display text-lg font-semibold">Thanks for subscribing!</p>
                <p className="mt-1 text-sm text-sage-600">
                  Check your inbox for a confirmation email.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-3 sm:flex-row"
                noValidate
              >
                <label htmlFor={`newsletter-email-${section.id}`} className="sr-only">
                  Email address
                </label>
                <input
                  id={`newsletter-email-${section.id}`}
                  type="email"
                  required
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={cn(
                    'flex-1 rounded-lg border border-sage-300 bg-surface px-4 py-3 text-sm text-ink placeholder:text-ink-muted',
                    'focus:outline-none focus:ring-2 focus:ring-sage-500'
                  )}
                  aria-invalid={status === 'error' ? 'true' : undefined}
                  aria-describedby={status === 'error' ? `newsletter-error-${section.id}` : undefined}
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className={cn(
                    'inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-medium',
                    'bg-cta text-cta-fg hover:bg-cta-deep transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 focus-visible:ring-offset-2',
                    'disabled:opacity-50 min-h-[44px]'
                  )}
                  aria-busy={status === 'loading'}
                >
                  {status === 'loading' && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  Subscribe
                </button>
              </form>
            )}

            {status === 'error' && (
              <p
                id={`newsletter-error-${section.id}`}
                className="mt-2 text-sm text-red-600"
                role="alert"
              >
                {errorMsg}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
